import React, { useState } from "react";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import { DataGrid, GridActionsCellItem, useGridApiRef } from "@mui/x-data-grid";
import { useNavigate } from "react-router-dom";
import { Stack, Divider, Chip } from "@mui/material";
import { convertTimeFormat } from "../../utils/timeFormatter";
import Dialog from "@mui/material/Dialog";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import Button from "@mui/material/Button";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import DOMPurify from 'dompurify';
import {CircularProgress} from "@mui/material";
import CustomPager from "../CustomPager";
import { useTranslation } from "react-i18next";

const columnHeader = (params) => {
  return (
    <Box>
      <Typography variant="h7" sx={{ fontWeight: "bold", color: "primary.main" }}>
        {params.colDef.headerName}
      </Typography>
    </Box>
  );
};

const DesktopMyNotifications = ({notifications, isLoading, markAsRead, markAllAsRead, unreadCount}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogData, setDialogData] = useState({
    sentBy: "",
    time: "",
    message: "",
    notificationId: null,
  });


  const handleCloseDialog = () => {
    setDialogData({});
    setOpenDialog(false);
  };

  const handleViewMessageClick = ({ message, sentBy, time, notificationId, isRead }) => {
    setDialogData({ sentBy, message, time, notificationId });
    setOpenDialog(true);
    // Mark as read when viewing
    if (!isRead && markAsRead && notificationId) {
      markAsRead(notificationId);
    }
  };

  // A notification about a request opens that request (and counts as read).
  const handleOrderNumberClick = (id, serviceName, notificationId, isRead) => {
    if (!isRead && markAsRead && notificationId) {
      markAsRead(notificationId);
    }
    navigate(`/order-details/${id}`, {
      state: { groupName: serviceName || t('notif_orderDetails') }
    });
  };
  const apiRef = useGridApiRef();
  const columns = [
    {
      field: "isRead",
      headerName: t('notif_status'),
      editable: false,
      width: 80,
      renderHeader: (params) => columnHeader(params),
      headerAlign: "center",
      headerClassName: "table-header",
      align: "center",
      renderCell: (params) => {
        return params.value ? (
          <Chip size="small" label={t('notif_read')} color="default" variant="outlined" />
        ) : (
          <Chip 
            size="small" 
            label={t('notif_new')} 
            color="primary" 
            icon={<FiberManualRecordIcon sx={{ fontSize: 10 }} />}
          />
        );
      },
    },
    {
      field: "timestamp",
      headerName: t('notif_sendTime'),
      editable: false,
      flex: 1,
      renderHeader: (params) => columnHeader(params),
      headerAlign: "center",
      headerClassName: "table-header",
      align: "center",
      valueGetter: (obj) => {
        return convertTimeFormat(obj.value);
      },
    },
    {
      field: "type",
      headerName: t('notif_type'),
      editable: false,
      flex: 1,
      renderHeader: (params) => columnHeader(params),
      headerAlign: "center",
      headerClassName: "table-header",
      align: "center",
      valueGetter: (obj) => {
        // A portal message also sent by SMS / email shows as one row: "البوابة + رسالة قصيرة".
        return [obj.row.type.name, ...(obj.row.alsoSentBy || [])].join(" + ");
      },
    },
    {
      field: "sentToName",
      headerName: t('notif_sentTo'),
      editable: false,
      flex: 1,
      renderHeader: (params) => columnHeader(params),
      headerAlign: "center",
      headerClassName: "table-header",
      align: "center",
    },
    {
      field: "sentTo",
      headerName: t('notif_sendInfo'),
      editable: false,
      flex: 1,
      renderHeader: (params) => columnHeader(params),
      headerAlign: "center",
      headerClassName: "table-header",
      align: "center",
    },
    {
      field: "subscriberServiceFullNumber",
      headerName: t('notif_orderNumber'),
      editable: false,
      flex: 1,
      renderHeader: (params) => columnHeader(params),
      headerAlign: "center",
      headerClassName: "table-header",
      align: "center",
      renderCell: (params) => {
        const hasNumber = Boolean(params.value);
        const appId = params.row?.subscriberServiceId;
        const canOpen = hasNumber && Boolean(appId);
        return (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: 1,
            }}
          >
            {hasNumber && <Typography>{params.value}</Typography>}
            {canOpen && (
              <GridActionsCellItem
                icon={<OpenInNewIcon color="primary" />}
                label="View"
                onClick={(e) => {
                  e.stopPropagation();
                  handleOrderNumberClick(appId, params.row?.serviceName, params.row.rowId, params.row.isRead);
                }}
              />
            )}
          </Box>
        );
      },
    },
    {
      field: "message",
      headerName: t('notif_message'),
      editable: false,
      flex: 1,
      renderHeader: (params) => columnHeader(params),
      headerAlign: "center",
      align: "center",
      headerClassName: "table-header",
      filterable: false,
      sortable: false,
      renderCell: (params) => {
        return (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <GridActionsCellItem
              icon={<VisibilityIcon  />}
              label="View"
              onClick={(e) => {
                e.stopPropagation();
                handleViewMessageClick({
                  message: params.value,
                  sentBy: params.row.sentByUser,
                  time: convertTimeFormat(params.row.timestamp),
                  notificationId: params.row.rowId,
                  isRead: params.row.isRead,
                });
              }}
            />
          </Box>
        );
      },
    },
  ];

  if (isLoading) {
    return (
      <Box sx={{ textAlign: "center" }}>
        <CircularProgress size={80} />
      </Box>
    );
  }

  if (!notifications || notifications.length === 0) {
    return (
      <Box sx={{ textAlign: "center", p: 5 }}>
        <Typography variant="h5" color="text.secondary" sx={{ mb: 2 }}>
          {t('notif_noNotifications')}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {t('notif_noNotificationsDesc')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: "100%",
         padding: 2 ,
        "& .table-header": {
          backgroundColor: "#DFE7F2",
          color: "#BF3C4E",
          border: "1px solid white",
        },
        "& .MuiDataGrid-columnHeaders": {
          overflowX: { xs: "scroll", md: "hidden" },
        },
        "& .MuiDataGrid-footerContainer": {
          direction: "ltr",
        },
        "& .unread-row": {
          backgroundColor: "rgba(191, 60, 78, 0.08)",
          fontWeight: "bold",
        },
        "& .MuiDataGrid-row:hover": {
          backgroundColor: "#d9e0eb",
        },
      }}
    >

      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        sx={{
          "& .MuiDialog-paper": {
            border: "2px solid #BF3C4E", // Set the border color here
            padding: "20px",
            borderRadius: "10px",
          },
        }}
      >
        <Stack gap={3} sx={{ marginBottom: 5 }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              gap: 2,
            }}
          >
            <Typography sx={{ fontWeight: "bold" }}>{t('notif_sentBy')} :</Typography>
            <Typography>{dialogData.sentBy}</Typography>
          </Box>
          <Divider sx={{ width: "100%" }} />
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              gap: 2,
            }}
          >
            <Typography sx={{ fontWeight: "bold" }}>{t('notif_sendTime')} :</Typography>
            <Typography>{dialogData.time}</Typography>
          </Box>
          <Divider sx={{ width: "100%" }} />
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              gap: 1,
              alignItems:{xs: "start", md: "center"} 
            }}
          >
            <Typography sx={{ fontWeight: "bold" }}>{t('notif_messageBody')}:</Typography>
            <Typography dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(dialogData.message) }}></Typography>
          </Box>
        </Stack>
        <Button
          type="sumbit"
          sx={{ width: "30%", alignSelf: "center" }}
          variant="contained"
          onClick={handleCloseDialog}
        >
          <Typography>{t('notif_close')}</Typography>
        </Button>
      </Dialog>
      <DataGrid
        rows={notifications.map((item, index) => ({ ...item, rowId: item.id || index, id: index }))}
        columns={columns}
        initialState={{
          pagination: {
            paginationModel: {
              pageSize: 10,
            },
          },
        }}
        apiRef={apiRef}
        slots={{
          pagination: CustomPager,
        }}
        pageSizeOptions={[5]}
        disableRowSelectionOnClick
        onRowClick={(params) => {
          const row = params.row;
          if (row.subscriberServiceId) {
            handleOrderNumberClick(row.subscriberServiceId, row.serviceName, row.rowId, row.isRead);
          } else {
            handleViewMessageClick({
              message: row.message,
              sentBy: row.sentByUser,
              time: convertTimeFormat(row.timestamp),
              notificationId: row.rowId,
              isRead: row.isRead,
            });
          }
        }}
        sx={{ "& .MuiDataGrid-row": { cursor: "pointer" } }}
        getRowClassName={(params) => !params.row.isRead ? 'unread-row' : ''}
      />
    </Box>
  );
};

export default DesktopMyNotifications;
